
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Sun, Zap, Package } from "lucide-react";
import { PainelForm } from "./PainelForm";
import { InversorForm } from "./InversorForm";
import { OutrosItensForm } from "./OutrosItensForm";
import { EquipamentosList } from "./EquipamentosList";
import { CategoriasOutrosList } from "./CategoriasOutrosList";
import { EquipamentoSolar } from "@/types";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";

interface ConfigManualSectionProps {
  paineis: EquipamentoSolar[];
  setPaineis: (paineis: EquipamentoSolar[]) => void;
  inversores: EquipamentoSolar[];
  setInversores: (inversores: EquipamentoSolar[]) => void;
  categoriasOutros: { categoria: string; itens: string[] }[];
  setCategoriasOutros: (categorias: { categoria: string; itens: string[] }[]) => void;
  marcasPaineis: string[];
  marcasInversores: string[];
  modelosPorMarca: Record<string, string[]>;
}

export const ConfigManualSection = ({
  paineis,
  setPaineis,
  inversores,
  setInversores,
  categoriasOutros,
  setCategoriasOutros,
  marcasPaineis,
  marcasInversores,
  modelosPorMarca
}: ConfigManualSectionProps) => {
  // Estados para o formulário de novo equipamento
  const [novoTipoEquipamento, setNovoTipoEquipamento] = useState<"painel" | "inversor" | "outro">("painel");
  const [marcaSelecionada, setMarcaSelecionada] = useState("");
  const [modeloSelecionado, setModeloSelecionado] = useState("");
  const [categoriaSelecionada, setCategoriaSelecionada] = useState("");
  const [itemPersonalizado, setItemPersonalizado] = useState(false);
  const [detalhesEquipamento, setDetalhesEquipamento] = useState<{
    potencia?: number;
    precoUnitario?: number;
    fornecedor?: string;
    garantia?: number;
    dimensoes?: {
      altura?: number;
      largura?: number;
      profundidade?: number;
    };
    descricao?: string;
  }>({});
  const [editandoEquipamento, setEditandoEquipamento] = useState<EquipamentoSolar | null>(null);

  // Manipulador para adicionar novo equipamento
  const handleAdicionarEquipamento = () => {
    if (novoTipoEquipamento === "painel" || novoTipoEquipamento === "inversor") {
      // Verificar campos obrigatórios
      if (!marcaSelecionada || (!modeloSelecionado && !itemPersonalizado)) {
        toast.error("Por favor, preencha a marca e o modelo");
        return;
      }

      if (!detalhesEquipamento.potencia || detalhesEquipamento.potencia <= 0) {
        toast.error("Por favor, informe a potência do equipamento");
        return;
      }

      // Criar novo equipamento
      const novoEquipamento: EquipamentoSolar = {
        id: editandoEquipamento ? editandoEquipamento.id : uuidv4(),
        tipo: novoTipoEquipamento,
        marca: marcaSelecionada,
        modelo: modeloSelecionado || `${marcaSelecionada} personalizado`,
        potencia: detalhesEquipamento.potencia || 0,
        precoUnitario: detalhesEquipamento.precoUnitario || 0,
        fornecedor: detalhesEquipamento.fornecedor || "",
        garantia: detalhesEquipamento.garantia || 0,
        dimensoes: detalhesEquipamento.dimensoes,
        descricao: detalhesEquipamento.descricao,
        disponibilidade: true,
        quantidade: 1
      };

      if (editandoEquipamento) {
        // Atualizar equipamento existente
        if (novoTipoEquipamento === "painel") {
          setPaineis(paineis.map(p => p.id === editandoEquipamento.id ? novoEquipamento : p));
        } else {
          setInversores(inversores.map(i => i.id === editandoEquipamento.id ? novoEquipamento : i));
        }
        toast.success(`${novoTipoEquipamento === "painel" ? "Painel" : "Inversor"} atualizado com sucesso!`);
      } else {
        // Adicionar novo equipamento
        if (novoTipoEquipamento === "painel") {
          setPaineis([...paineis, novoEquipamento]);
        } else {
          setInversores([...inversores, novoEquipamento]);
        }
        toast.success(`${novoTipoEquipamento === "painel" ? "Painel" : "Inversor"} adicionado com sucesso!`);
      }

      // Limpar campos do formulário
      setEditandoEquipamento(null);
      setMarcaSelecionada("");
      setModeloSelecionado("");
      setItemPersonalizado(false);
      setDetalhesEquipamento({});
    } else if (novoTipoEquipamento === "outro") {
      // Verificar se a categoria e o item foram selecionados
      if (!categoriaSelecionada || (!modeloSelecionado && !itemPersonalizado)) {
        toast.error("Por favor, selecione a categoria e o item");
        return;
      }

      // Verificar se item já existe
      const categoriaExistente = categoriasOutros.find(cat => cat.categoria === categoriaSelecionada);
      
      if (categoriaExistente) {
        // Verificar se item já existe na categoria
        if (!itemPersonalizado && categoriaExistente.itens.includes(modeloSelecionado)) {
          toast.error("Este item já existe na categoria selecionada");
          return;
        }
        
        // Adicionar item à categoria existente
        const novoItem = itemPersonalizado ? `${detalhesEquipamento.descricao || "Item personalizado"}` : modeloSelecionado;
        
        setCategoriasOutros(categoriasOutros.map(cat => 
          cat.categoria === categoriaSelecionada 
            ? { ...cat, itens: [...cat.itens, novoItem] } 
            : cat
        ));
      } else {
        // Criar nova categoria com o item
        const novoItem = itemPersonalizado ? `${detalhesEquipamento.descricao || "Item personalizado"}` : modeloSelecionado;
        setCategoriasOutros([...categoriasOutros, { 
          categoria: categoriaSelecionada, 
          itens: [novoItem] 
        }]);
      }
      
      toast.success("Item adicionado com sucesso!");
      
      // Limpar campos do formulário
      setCategoriaSelecionada("");
      setModeloSelecionado("");
      setItemPersonalizado(false);
      setDetalhesEquipamento({});
    }
  };

  // Manipulador para editar equipamento
  const handleEditarEquipamento = (equipamento: EquipamentoSolar) => {
    setNovoTipoEquipamento(equipamento.tipo as "painel" | "inversor");
    setMarcaSelecionada(equipamento.marca);
    setModeloSelecionado(equipamento.modelo);
    setDetalhesEquipamento({
      potencia: equipamento.potencia,
      precoUnitario: equipamento.precoUnitario,
      fornecedor: equipamento.fornecedor,
      garantia: equipamento.garantia,
      dimensoes: equipamento.dimensoes,
      descricao: equipamento.descricao
    });
    setEditandoEquipamento(equipamento);
  };

  // Manipulador para excluir equipamento
  const handleExcluirEquipamento = (id: string, tipo: "painel" | "inversor") => {
    if (tipo === "painel") {
      setPaineis(paineis.filter(p => p.id !== id));
      toast.success("Painel excluído com sucesso!");
    } else {
      setInversores(inversores.filter(i => i.id !== id));
      toast.success("Inversor excluído com sucesso!");
    }
  };

  // Manipulador para excluir item de categoria
  const handleExcluirItemCategoria = (categoria: string, item: string) => {
    const categoriaAtualizada = categoriasOutros.find(cat => cat.categoria === categoria);
    
    if (categoriaAtualizada) {
      const itensAtualizados = categoriaAtualizada.itens.filter(i => i !== item);
      
      if (itensAtualizados.length === 0) {
        // Se não sobrar nenhum item, remove a categoria inteira
        setCategoriasOutros(categoriasOutros.filter(cat => cat.categoria !== categoria));
      } else {
        // Atualiza apenas os itens da categoria
        setCategoriasOutros(categoriasOutros.map(cat => 
          cat.categoria === categoria 
            ? { ...cat, itens: itensAtualizados } 
            : cat
        ));
      }
      
      toast.success("Item removido com sucesso!");
    }
  };

  return (
    <div className="mt-6">
      <Card>
        <CardHeader>
          <CardTitle>Cadastro de Equipamentos</CardTitle>
          <CardDescription>
            Cadastre e gerencie painéis, inversores e outros equipamentos para uso na calculadora solar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="paineis" className="mt-2">
            <TabsList className="grid grid-cols-3 gap-4 w-full h-auto p-1">
              <TabsTrigger 
                value="paineis" 
                className="flex flex-col gap-1 py-3 md:py-4 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
                onClick={() => setNovoTipoEquipamento("painel")}
              >
                <Sun className="h-5 w-5" />
                <span className="text-xs sm:text-sm">Painéis Solares</span>
              </TabsTrigger>
              <TabsTrigger 
                value="inversores" 
                className="flex flex-col gap-1 py-3 md:py-4 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
                onClick={() => setNovoTipoEquipamento("inversor")}
              >
                <Zap className="h-5 w-5" />
                <span className="text-xs sm:text-sm">Inversores</span>
              </TabsTrigger>
              <TabsTrigger 
                value="outros" 
                className="flex flex-col gap-1 py-3 md:py-4 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
                onClick={() => setNovoTipoEquipamento("outro")}
              >
                <Package className="h-5 w-5" />
                <span className="text-xs sm:text-sm">Outros Itens</span>
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="paineis" className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PainelForm 
                  marcasPaineis={marcasPaineis}
                  modelosPorMarca={modelosPorMarca}
                  marcaSelecionada={marcaSelecionada}
                  setMarcaSelecionada={setMarcaSelecionada}
                  modeloSelecionado={modeloSelecionado}
                  setModeloSelecionado={setModeloSelecionado}
                  itemPersonalizado={itemPersonalizado}
                  setItemPersonalizado={setItemPersonalizado}
                  detalhesEquipamento={detalhesEquipamento}
                  setDetalhesEquipamento={setDetalhesEquipamento}
                  editandoEquipamento={editandoEquipamento}
                  handleAdicionarEquipamento={handleAdicionarEquipamento}
                />
                
                <EquipamentosList 
                  equipamentos={paineis}
                  tipo="painel"
                  onEdit={handleEditarEquipamento}
                  onDelete={handleExcluirEquipamento}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="inversores" className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <InversorForm 
                  marcasInversores={marcasInversores}
                  modelosPorMarca={modelosPorMarca}
                  marcaSelecionada={marcaSelecionada}
                  setMarcaSelecionada={setMarcaSelecionada}
                  modeloSelecionado={modeloSelecionado}
                  setModeloSelecionado={setModeloSelecionado}
                  itemPersonalizado={itemPersonalizado}
                  setItemPersonalizado={setItemPersonalizado}
                  detalhesEquipamento={detalhesEquipamento}
                  setDetalhesEquipamento={setDetalhesEquipamento}
                  editandoEquipamento={editandoEquipamento}
                  handleAdicionarEquipamento={handleAdicionarEquipamento}
                />
                
                <EquipamentosList 
                  equipamentos={inversores}
                  tipo="inversor"
                  onEdit={handleEditarEquipamento}
                  onDelete={handleExcluirEquipamento}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="outros" className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <OutrosItensForm 
                  categoriaSelecionada={categoriaSelecionada}
                  setCategoriaSelecionada={setCategoriaSelecionada}
                  modeloSelecionado={modeloSelecionado}
                  setModeloSelecionado={setModeloSelecionado}
                  itemPersonalizado={itemPersonalizado}
                  setItemPersonalizado={setItemPersonalizado}
                  detalhesEquipamento={detalhesEquipamento}
                  setDetalhesEquipamento={setDetalhesEquipamento}
                  categoriasOutros={categoriasOutros}
                  handleAdicionarEquipamento={handleAdicionarEquipamento}
                />
                
                <CategoriasOutrosList 
                  categorias={categoriasOutros}
                  onDeleteItem={handleExcluirItemCategoria}
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
